import React, { useMemo, useState } from 'react';
import { Button, Input, Select, Switch, Spin  } from 'antd';
import { ArrowRightOutlined , ArrowLeftOutlined } from "@ant-design/icons";
import { getCurrencySymbolForStore } from '../utils/currency';
import useSkuSearch from '../hooks/useSkuSearch';

const { Option } = Select;

const StepBenefitMechanic = ({ onNext, onBack, formData, updateField }) => {
  const [errors, setErrors] = useState({});
  const giftEnabled = Boolean(formData.giftEnabled);
  const discountEnabled = Boolean(formData.discountEnabled);
  const giftSkuCount = Array.isArray(formData.giftSkus) ? formData.giftSkus.length : 0;
  const currencySymbol = getCurrencySymbolForStore(formData.storefront);

  const selectedGiftSkus = useMemo(
    () => (Array.isArray(formData.giftSkus) ? formData.giftSkus : []),
    [formData.giftSkus]
  );

  const {
    options: giftSkuSelectOptions,
    onSearch: searchSkus,
    onPopupScroll: onGiftSkuPopupScroll,
    loading: skuSearchLoading,
    loadingMore: giftSkuLoadingMore,
    hasMore: giftSkuHasMore,
  } = useSkuSearch({ storeLabel: formData.storefront, selectedSkus: selectedGiftSkus });

  const handleNext = () => {
    const nextErrors = {
      mechanicSelection: !giftEnabled && !discountEnabled,
    };

    if (giftEnabled) {
      const minCartValue = String(formData.minCartValue ?? '').trim();
      // Whole numbers only, no decimal point.
      nextErrors.minCartValue = !/^\d+$/.test(minCartValue);
      nextErrors.giftSkus = !formData.giftSkus?.length;
      // Gifts Per Order can't be more than the number of Gift SKUs.
      const giftsPerOrder = Number(formData.giftsPerOrder);
      nextErrors.giftsPerOrder = !(giftsPerOrder >= 1 && giftsPerOrder <= giftSkuCount);
    }

    if (discountEnabled) {
      nextErrors.discountType = !formData.discountType;
      nextErrors.discountValue = !/^\d+$/.test(String(formData.discountValue ?? '').trim());
    }

    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;
    onNext();
  };

  const handleGiftSkusChange = (skus) => {
    handleFieldChange('giftSkus', skus);
    // Gifts Per Order follows the SKU count automatically.
    handleFieldChange('giftsPerOrder', skus.length ? String(skus.length) : '');
  };

  const handleFieldChange = (field, value) => {
    updateField(field, value);
    if (errors[field]) setErrors((current) => ({ ...current, [field]: false }));
  };

  const handleSwitchChange = (field, checked, dependentFields) => {
    updateField(field, checked);
    const otherMechanicEnabled = field === 'giftEnabled' ? discountEnabled : giftEnabled;
    setErrors((current) => ({
      ...current,
      mechanicSelection: !checked && !otherMechanicEnabled,
      ...(!checked && Object.fromEntries(dependentFields.map((dependentField) => [dependentField, false]))),
    }));
  };

  return (
    <div className='step-container'>
      <div className='step-content'>
        <div className='step-header'>          
          <div className='step-title'>Benefit & Mechanic</div>
          <div className='step-subtitle'>Choose how this rule rewards the customer.</div>
        </div>

        <div className='step-form'>
            
          <div className='step-group'>
            <div className='step-form-group-inner d-flex d-flex-row alignCenter gap-8'>
              <Switch
                checked={formData.giftEnabled}
                size="small"
                onChange={(checked) => handleSwitchChange('giftEnabled', checked, ['minCartValue', 'giftSkus', 'giftsPerOrder'])}
              />
              <span className='step-switch-label'>Gift</span>
              </div>
              <div className='step-form-hint-dark'>
                List the GWP SKUs available for this rule.
              </div>
          </div>

          <div className='step-group'>
            <div className='step-form-label'>Minimum Cart Value to Get the Free Gift <span style={{ color: '#ff4d4f' }}>*</span></div>
            <Input
              type="text"
              inputMode="decimal"
              placeholder="Enter Minimum Cart Value"
              prefix={
                <span
                  style={{
                    borderRight: '1px solid #D9D9D9',
                    padding: '0px 7px',
                    background: 'rgba(0, 0, 0, .06)',
                    borderRadius: '50%',
                    borderLeft: '1px solid #D9D9D9',
                  }}
                >
                  {currencySymbol}
                </span>
              }
              value={formData.minCartValue}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) handleFieldChange('minCartValue', value);
              }}
              status={errors.minCartValue ? 'error' : undefined}
              className='Input-border InputText'
              disabled={!giftEnabled}
            />
            {errors.minCartValue && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Minimum Cart Value is required (whole number, no decimals).</div>}
          </div>

          <div className='step-group'>
            <div className='step-form-label'>Select/Add Gift SKUs <span style={{ color: '#ff4d4f' }}>*</span></div>
            <span className='step-form-hint-dark'>Red SKUs are not visible to customers on the storefront (Visibility OFF), while Black SKUs are visible to customers (Visibility ON).</span>
            <Select
              mode="multiple"
              showSearch
              filterOption={false}
              onSearch={searchSkus}
              onPopupScroll={onGiftSkuPopupScroll}
              notFoundContent={
                skuSearchLoading
                  ? <span><Spin size="small" style={{ marginRight: 8 }} />Loading SKUs...</span>
                  : (formData.storefront ? 'No SKUs found' : 'Select a Store first')
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {giftSkuLoadingMore && (
                    <div style={{ textAlign: 'center', padding: 8, color: 'rgba(0,0,0,0.45)' }}>
                      <Spin size="small" style={{ marginRight: 8 }} />Loading more SKUs...
                    </div>
                  )}
                  {!giftSkuLoadingMore && !giftSkuHasMore && giftSkuSelectOptions.length > 0 && (
                    <div style={{ textAlign: 'center', padding: 8, color: 'rgba(0,0,0,0.45)' }}>
                      No more SKUs to load
                    </div>
                  )}
                </>
              )}
              placeholder="Search and select Gift SKUs"
              options={giftSkuSelectOptions}
              value={selectedGiftSkus}
              onChange={handleGiftSkusChange}
              status={errors.giftSkus ? 'error' : undefined}
              className='Input-border InputSelect w-100 height-auto'
              style={{ width: '100%' }}
              disabled={!giftEnabled || !formData.storefront}
            />
            {errors.giftSkus && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>At least one Gift SKU is required.</div>}
            {!formData.storefront && <div className='step-form-hint'>Select a Store in Basic Info to search Gift SKUs.</div>}
          </div>

          <div className='step-group'>
            <div className='step-form-label'>Gifts Per Order <span style={{ color: '#ff4d4f' }}>*</span></div>
            <Select              
              value={formData.giftsPerOrder || undefined}
              placeholder={giftSkuCount ? 'Select' : 'Add Gift SKUs first'}
              onChange={(value) => handleFieldChange('giftsPerOrder', value)}
              status={errors.giftsPerOrder ? 'error' : undefined}
              className='Input-border InputSelect w-100'
              disabled
            >
              {Array.from({ length: giftSkuCount }, (_, index) => String(index + 1)).map((count) => (
                <Option key={count} value={count}>{count}</Option>
              ))}
            </Select>
            {errors.giftsPerOrder && (
              <div className='step-form-hint' style={{ color: '#ff4d4f' }}>
                {giftSkuCount ? `Gifts Per Order must be between 1 and ${giftSkuCount}.` : 'Add Gift SKUs first.'}
              </div>
            )}
            <div className='step-form-hint'>
              How many of the {giftSkuCount || 0} SKUs above the customer receives (max {giftSkuCount || 0}).
            </div>
          </div>

          <div className='step-group'>
            <div className='step-form-group-inner d-flex d-flex-row alignCenter gap-8'>
              <Switch
                checked={formData.discountEnabled}
                size="small"
                onChange={(checked) => handleSwitchChange('discountEnabled', checked, ['discountType', 'discountValue'])}
              />
              <span className='step-switch-label'>Discount</span>
            </div>
          </div>
          {errors.mechanicSelection && (
            <div className='step-form-hint' style={{ color: '#ff4d4f' }}>
              Enable Gift or Discount to continue.
            </div>
          )}

          <div className='step-group d-flex d-flex-row gap-16'>
            <div className='w-50 step-group'>
              <div className='step-form-label'>Discount Type <span style={{ color: '#ff4d4f' }}>*</span></div>
                <Select
                  value={formData.discountType}
                  onChange={(value) => handleFieldChange('discountType', value)}
                  status={errors.discountType ? 'error' : undefined}
                  className='Input-border InputSelect w-100'
                  disabled={!discountEnabled}
                >
                  <Option value="percentage">Percentage</Option>
                  <Option value="fixed">Fixed</Option>
                </Select>
                {errors.discountType && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Discount Type is required.</div>}
            </div>
            <div className='w-50 step-group'>
              <div className='step-form-label'>Discount Value <span style={{ color: '#ff4d4f' }}>*</span></div>
              <Input
                placeholder="Enter Discount Value"
                value={formData.discountValue}
                inputMode='numeric'
                onChange={(e) => {
                  const { value } = e.target;
                  if (/^\d*$/.test(value)) handleFieldChange('discountValue', value);
                }}
                status={errors.discountValue ? 'error' : undefined}
                className='Input-border'
                disabled={!discountEnabled}
              />
              {errors.discountValue && <div className='step-form-hint' style={{ color: '#ff4d4f' }}>Discount Value is required (whole number, no decimals).</div>}
            </div>
          </div>
        </div>

        <div className='step-footer'>
          <Button onClick={onBack} className='btn-s'>
            <ArrowLeftOutlined />
            Previous: Basic Info
          </Button>
          <Button type="primary" onClick={handleNext} className='btn-p'>
            Next: Eligibility Conditions 
            <ArrowRightOutlined />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepBenefitMechanic;
